import type { BulkWriteOutcome } from '@/lib/projection-cell-db'
import { formatProjectionCount } from '@/lib/projection-cell'

/** A toast of the "Manage projections" modal, worded from what a write actually returned. */
export interface Notice {
  tone: 'success' | 'error'
  title: string
  description?: string
}

export const MOVED_OUT_OF_PERIOD = 'Projection moved out of this period'

export const ADDED_OUTSIDE_PERIOD = 'It is outside this period, so it is no longer shown here.'

export const NONE_UPDATED =
  'None of the selected projections could be updated — they may have been changed or deleted. The list was refreshed.'

const ROW_NOT_MOVED =
  'The projection could not be moved — it may have been changed or deleted. The list was refreshed.'

const OTHERS_CHANGED = 'The others were changed or deleted meanwhile. The list was refreshed.'

/** The error notice when not every requested row was written (raw error per ADR 0006), else null. */
function shortfall(outcome: BulkWriteOutcome): Notice | null {
  if (outcome.updated === 0 && !outcome.firstError) return { tone: 'error', title: NONE_UPDATED }
  if (!outcome.firstError && outcome.updated >= outcome.requested) return null
  return {
    tone: 'error',
    title: `Updated ${outcome.updated} of ${formatProjectionCount(outcome.requested)}`,
    description: outcome.firstError?.message ?? OTHERS_CHANGED,
  }
}

function movedOut(n: number): string {
  return n === 1 ? MOVED_OUT_OF_PERIOD : `${n} projections moved out of this period`
}

/** After setting one date on the selected rows; `leftPeriod`: that date is outside the period. */
export function bulkSetDateNotice(outcome: BulkWriteOutcome, leftPeriod: boolean): Notice {
  return (
    shortfall(outcome) ?? {
      tone: 'success',
      title: leftPeriod ? movedOut(outcome.updated) : `Date updated for ${formatProjectionCount(outcome.updated)}`,
    }
  )
}

/** After moving the selected rows a day later; `leaving`: how many land past the period's end. */
export function bulkNextDayNotice(outcome: BulkWriteOutcome, leaving: number): Notice {
  const n = outcome.updated
  const notice = shortfall(outcome)
  if (notice) return notice
  if (leaving === n) return { tone: 'success', title: movedOut(n) }
  return {
    tone: 'success',
    title: n === 1 ? 'Projection moved to the next day' : `${n} projections moved to the next day`,
    description: leaving > 0 ? `${leaving} moved out of this period` : undefined,
  }
}

/** After "Move to next day" on one row; `leftPeriod`: the next day is past the period's end. */
export function rowNextDayNotice(outcome: BulkWriteOutcome, leftPeriod: boolean): Notice {
  if (outcome.updated === 0 && !outcome.firstError) return { tone: 'error', title: ROW_NOT_MOVED }
  const notice = shortfall(outcome)
  if (notice) return notice
  return { tone: 'success', title: leftPeriod ? MOVED_OUT_OF_PERIOD : 'Projection moved to the next day' }
}
